import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { concatMap, EMPTY, finalize, from, Observable, race, take } from 'rxjs';

import { FileResult, UploaderOptions } from '../../domain/public-api';

const IMAGE_TYPES = [
  '.bmp',
  '.gif',
  '.jpeg',
  '.jpg',
  '.png',
  '.svg',
  '.tif',
  '.tiff',
  '.webp',
] as const;
const PDF_TYPES = ['.pdf'] as const;
const VIDEO_TYPES = ['.mov', '.mp4', '.mpg', '.mpeg', '.webm', '.wmv'] as const;

@Injectable({ providedIn: 'root' })
export class UploaderFacade {
  private readonly overlay = inject(Overlay);

  private overlayRef?: OverlayRef;

  get supportedImageTypes(): readonly string[] {
    return [...IMAGE_TYPES];
  }

  get supportedPdfTypes(): readonly string[] {
    return [...PDF_TYPES];
  }

  get supportedVideoTypes(): readonly string[] {
    return [...VIDEO_TYPES];
  }

  upload(options?: UploaderOptions): Observable<void | FileResult[]> {
    return from(import('../uploader-component/uploader.component')).pipe(
      concatMap(({ UploaderComponent }) => {
        if (this.overlayRef) {
          return EMPTY;
        }

        this.overlayRef = this.overlay.create();
        const portal = new ComponentPortal(UploaderComponent);
        const componentRef = this.overlayRef.attach(portal);

        // Pass the configuration
        componentRef.setInput('options', options);

        // Handle cleanup on success or cancel
        const success$ = outputToObservable(componentRef.instance.uploadSuccess);
        const cancel$ = outputToObservable(componentRef.instance.uploadCancel);
        const waitForClose$ = race([success$, cancel$]).pipe(take(1));

        return waitForClose$.pipe(
          finalize(() => {
            this.overlayRef?.detach();
            this.overlayRef?.dispose();
            this.overlayRef = undefined;
          }),
        );
      }),
    );
  }
}
